import { useState } from 'react'

export type TransferFormValues = {
  toTeamId: string
  effectiveDate: string
  reason?: string
}

/**
 * docs/product/scheduling-model.md's "Effective-dated changes": "A
 * transfer to another team or shift takes an effective date." The
 * destination is any other open team, chosen here; the form does not know
 * which team the employees are on today, so the caller passes only the
 * teams they can move to. Shared by the single-employee transfer on the
 * profile and the bulk transfer on the directory.
 */
export default function TransferForm({
  destinations,
  minEffectiveDate,
  onSave,
  onCancel,
}: {
  destinations: { id: string; name: string; openDate: string; closeDate: string | null }[]
  minEffectiveDate?: string
  onSave: (values: TransferFormValues) => void
  onCancel: () => void
}) {
  const [chosenTeamId, setToTeamId] = useState(destinations[0]?.id ?? '')
  const [effectiveDate, setEffectiveDate] = useState('')
  const [reason, setReason] = useState('')

  // A team is offered while it is open on the effective date, so a temporary
  // team stays selectable until its close date. With no date chosen yet,
  // every team still open is offered. A choice the date has since ruled out
  // falls back to the first team offered.
  const available = destinations.filter(
    (team) => !effectiveDate || (team.openDate <= effectiveDate && (team.closeDate === null || team.closeDate >= effectiveDate)),
  )
  const toTeamId = available.some((team) => team.id === chosenTeamId) ? chosenTeamId : (available[0]?.id ?? '')

  const canSave = toTeamId !== '' && effectiveDate !== '' && (!minEffectiveDate || effectiveDate >= minEffectiveDate)

  return (
    <div className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="transfer-team" className="mb-1 block text-caption font-bold text-ink-muted">
            Destination team
          </label>
          <select
            id="transfer-team"
            value={toTeamId}
            onChange={(event) => setToTeamId(event.target.value)}
            className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
          >
            {available.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="transfer-effective-date" className="mb-1 block text-caption font-bold text-ink-muted">
            Effective date
          </label>
          <input
            id="transfer-effective-date"
            type="date"
            min={minEffectiveDate}
            value={effectiveDate}
            onChange={(event) => setEffectiveDate(event.target.value)}
            className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
          />
          {minEffectiveDate && (
            <p className="mt-1 text-caption text-ink-subtle">
              No earlier than {minEffectiveDate}: an active detail holds the earliest date to the pay period after it
              ends.
            </p>
          )}
        </div>
      </div>
      <div>
        <label htmlFor="transfer-reason" className="mb-1 block text-caption font-bold text-ink-muted">
          Reason (optional)
        </label>
        <input
          id="transfer-reason"
          type="text"
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => canSave && onSave({ toTeamId, effectiveDate, reason: reason.trim() || undefined })}
          disabled={!canSave}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          Transfer
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
